import React, { useState } from "react";
import {
  Anchor,
  Button,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { Link } from "react-router-dom";
import { useTable } from "@refinedev/core";
import { IconSearch } from "@tabler/icons-react";

import type { Restaurant } from "../../types";

export function RestaurantList() {
  const [searchValue, setSearchValue] = useState("");

  const {
    tableQueryResult,
    current,
    setCurrent,
    pageCount,
    setFilters,
  } = useTable<Restaurant>({
    resource: "restaurants",
    pagination: {
      current: 1,
      pageSize: 10,
    },
    filters: {
      initial: [],
    },
  });

  const isLoading = tableQueryResult.isLoading;
  const records = tableQueryResult.data?.data ?? [];

  // Handle search with debounce
  const handleSearch = (value: string) => {
    setSearchValue(value);
    setFilters([
      {
        field: "search",
        operator: "eq",
        value: value || undefined,
      },
    ]);
    setCurrent(1); // Reset to first page on search
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Restaurants</Title>
        <Button component={Link} to="/admin/restaurants/create">
          Create restaurant
        </Button>
      </Group>

      <TextInput
        placeholder="Search restaurants by name, description, or admin notes..."
        leftSection={<IconSearch size={16} />}
        value={searchValue}
        onChange={(e) => handleSearch(e.currentTarget.value)}
        style={{ maxWidth: 400 }}
      />

      {isLoading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : (
        <>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Menus</Table.Th>
                <Table.Th>Updated</Table.Th>
                <Table.Th style={{ width: 180 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {records.map((restaurant: Restaurant) => (
                <Table.Tr key={restaurant.id}>
                  <Table.Td>
                    <Text fw={600}>{restaurant.name ?? "(Unnamed)"}</Text>
                    {restaurant.description ? (
                      <Text c="dimmed" size="sm" lineClamp={2}>
                        {restaurant.description}
                      </Text>
                    ) : null}
                  </Table.Td>
                  <Table.Td>
                    <Text>{typeof restaurant.menus === 'number' ? restaurant.menus : (restaurant.menus?.length ?? 0)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {restaurant.updatedAt ? new Date(restaurant.updatedAt).toLocaleString() : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Anchor component={Link} to={`/admin/restaurants/edit/${restaurant.id}`}>
                        Edit
                      </Anchor>
                      <Anchor component={Link} to={`/admin/restaurants/show/${restaurant.id}`}>
                        View
                      </Anchor>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {pageCount > 1 ? (
            <Group justify="center">
              <Pagination total={pageCount} value={current} onChange={setCurrent} />
            </Group>
          ) : null}
        </>
      )}
    </Stack>
  );
}
